import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../../context/AuthContext';
import RequesterOverview from './RequesterOverview';
import CreateRequest from './CreateRequest';
import ActiveRequest from './ActiveRequest';
import RequestHistory from './RequestHistory';
import RequesterProfile from './RequesterProfile';
import OverallStats from './OverallStats';
import api from '../../../config/api';

const RequesterDashboard = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [activeRequests, setActiveRequests] = useState([]);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [showOverallStats, setShowOverallStats] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalRequests: 0,
      activeRequests: 0,
      completedRequests: 0,
      totalDonors: 0
    },
    recentRequests: [],
    bloodTypeRequirements: [],
    statusDistribution: {
      pending: 0,
      in_progress: 0,
      fulfilled: 0,
      partially_fulfilled: 0,
      cancelled: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current section based on URL
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === '/requester-dashboard') return 'overview';
    if (path.includes('/requester-dashboard/create')) return 'create';
    if (path.includes('/requester-dashboard/active')) return 'active';
    if (path.includes('/requester-dashboard/history')) return 'history';
    if (path.includes('/requester-dashboard/profile')) return 'profile';
    if (path.includes('/requester-dashboard/stats')) return 'stats';
    return 'overview';
  };

  // Fetch dashboard data when component mounts
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Use the api client instead of direct axios calls
      const response = await api.get('/dashboard/requester');
      setDashboardData(response.data);
      
      // Also fetch active requests
      const activeRequestsResponse = await api.get('/requests/me');
      setActiveRequests(activeRequestsResponse.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  // Toggle overall stats view
  const toggleOverallStats = () => {
    setShowOverallStats(!showOverallStats);
  };

  // Render content based on current section
  const renderContent = () => {
    const section = getCurrentSection();
    
    if (showNewRequestForm) {
      return <CreateRequest 
        setShowNewRequestForm={setShowNewRequestForm}
        activeRequests={activeRequests}
        setActiveRequests={setActiveRequests}
        onSuccess={fetchDashboardData}
      />;
    }
    
    if (showOverallStats || section === 'stats') {
      return (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Overall Platform Statistics</h2>
            <button 
              onClick={() => setShowOverallStats(false)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700 text-sm font-medium"
            >
              Back to Dashboard
            </button>
          </div>
          <OverallStats />
        </div>
      );
    }
    
    switch (section) {
      case 'create':
        return <CreateRequest 
          setShowNewRequestForm={setShowNewRequestForm}
          activeRequests={activeRequests}
          setActiveRequests={setActiveRequests}
          onSuccess={fetchDashboardData}
        />;
      case 'active':
        return <ActiveRequest onStatusChange={fetchDashboardData} />;
      case 'history':
        return <RequestHistory />;
      case 'profile':
        return <RequesterProfile />;
      default:
        return (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Your Dashboard</h2>
              <button 
                onClick={toggleOverallStats}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white text-sm font-medium"
              >
                View Platform Statistics
              </button>
            </div>
            <RequesterOverview 
              setShowNewRequestForm={setShowNewRequestForm} 
              dashboardStats={dashboardData.stats}
              recentRequests={dashboardData.recentRequests}
              bloodTypeRequirements={dashboardData.bloodTypeRequirements}
              statusDistribution={dashboardData.statusDistribution}
              loading={loading}
              error={error}
            />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content Container */}
      <div className="relative pt-6 md:pt-8 px-6 pb-6">
        {renderContent()}
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default RequesterDashboard;